import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, Clock, FileText, AlertCircle, CheckCircle, Upload, User, Building2, Users, X, ChevronDown } from 'lucide-react';
import { createActivity, uploadActivityFile, getUsers, getBagian } from '../../api';
import { useAuth } from '../../AuthContext';

const categoryOptions = ['Work', 'Development', 'Documentation', 'Meeting', 'Other'];
const priorityOptions = ['Low', 'Medium', 'High', 'Urgent'];
const statusOptions = ['Pending', 'InProgress', 'Completed'];

export default function AddActivity() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [bagianList, setBagianList] = useState([]);
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'Work',
    priority: 'Medium',
    tanggal: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    bagian: 'Umum',
    status: 'Pending',
    assigned_to: '',
  });

  // Multi-tagging state
  const [selectedBagianIds, setSelectedBagianIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [taggedUsers, setTaggedUsers] = useState([]); // resolved users from bagian + direct

  // Dropdown open state
  const [bagianDropdownOpen, setBagianDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const bagianRef = useRef(null);
  const userRef = useRef(null);

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = async (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    setFile(selected);
    setFileName(selected.name);
  };

  // Toggle bagian selection
  const toggleBagian = (bagianId) => {
    setSelectedBagianIds(prev =>
      prev.includes(bagianId)
        ? prev.filter(id => id !== bagianId)
        : [...prev, bagianId]
    );
  };

  // Toggle user selection
  const toggleUser = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Remove a tagged user
  const removeTaggedUser = (userId) => {
    // Find if user came from bagian or direct
    setSelectedUserIds(prev => prev.filter(id => id !== userId));
    // Also remove from bagian-based selection if user's bagian is selected
    const user = users.find(u => u.id === userId);
    if (user?.bagian_id && selectedBagianIds.includes(user.bagian_id)) {
      toggleBagian(user.bagian_id);
    }
  };

  // Resolve tagged users for display
  useEffect(() => {
    const resolved = [];
    const seen = new Set();

    // Add users from selected bagian
    if (selectedBagianIds.length > 0) {
      users.forEach(u => {
        if (u.bagian_id && selectedBagianIds.includes(u.bagian_id) && !seen.has(u.id) && u.is_active !== false) {
          seen.add(u.id);
          const bagianName = bagianList.find(b => b.id === u.bagian_id)?.name || '';
          resolved.push({ ...u, tagSource: bagianName ? `via ${bagianName}` : 'via Bagian' });
        }
      });
    }

    // Add directly selected users
    selectedUserIds.forEach(uid => {
      if (!seen.has(uid)) {
        seen.add(uid);
        const u = users.find(user => user.id === uid);
        if (u) {
          resolved.push({ ...u, tagSource: 'Langsung' });
        }
      }
    });

    setTaggedUsers(resolved);
  }, [selectedBagianIds, selectedUserIds, users, bagianList]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Nama activity harus diisi');
      return;
    }
    setSaving(true);
    setError('');
    try {
      let attachment = '';
      // Upload file first if selected
      if (file) {
        setUploading(true);
        const uploadRes = await uploadActivityFile(file);
        attachment = uploadRes.data.filename || '';
        setUploading(false);
      }

      // Format start_time and end_time as ISO strings
      const payload = { ...form };
      if (payload.start_time) {
        // Convert from datetime-local format to ISO
        payload.start_time = payload.start_time.includes('T')
          ? payload.start_time
          : payload.start_time + ':00';
      } else {
        payload.start_time = null;
      }
      if (payload.end_time) {
        payload.end_time = payload.end_time.includes('T')
          ? payload.end_time
          : payload.end_time + ':00';
      } else {
        payload.end_time = null;
      }
      payload.attachment = attachment;

      // Send assigned_to as int or null
      payload.assigned_to = payload.assigned_to ? parseInt(payload.assigned_to, 10) : null;

      // Add tagging data
      payload.tagged_bagian_ids = selectedBagianIds;
      payload.tagged_user_ids = selectedUserIds;

      await createActivity(payload);
      navigate('/activity');
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan activity');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (bagianRef.current && !bagianRef.current.contains(e.target)) {
        setBagianDropdownOpen(false);
      }
      if (userRef.current && !userRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load users & bagian for dropdowns, default assigned_to to current user
  useEffect(() => {
    // Always default assigned_to to current logged-in user (works for all roles)
    if (currentUser?.id) {
      setForm(prev => ({ ...prev, assigned_to: String(currentUser.id) }));
    }
    // Load users list
    getUsers().then(res => {
      setUsers(res.data || []);
    }).catch(() => {
      // If getUsers() fails (non-admin), at least include current user in dropdown
      if (currentUser?.id) {
        setUsers([currentUser]);
      }
    });
    getBagian().then(res => setBagianList(res.data || [])).catch(() => {});
  }, [currentUser]);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Plus size={24} />
              Add New Activity
            </h2>
            <p className="page-desc">Buat activity baru untuk melacak pekerjaan harian</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: 16 }}>
          <AlertCircle size={14} /><span>{error}</span>
        </div>
      )}

      <div style={{ maxWidth: 800 }}>
        <form onSubmit={handleSubmit} style={{ background: 'white', borderRadius: 12, padding: 28, border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Nama Activity */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Activity Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Masukkan nama activity"
                style={inputStyle}
                required
              />
            </div>

            {/* Description */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Deskripsi activity"
                rows={8}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 200 }}
              />
            </div>

            {/* Category */}
            <div>
              <label style={labelStyle}>Category</label>
              <select name="category" value={form.category} onChange={handleChange} style={inputStyle}>
                {categoryOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label style={labelStyle}>Priority</label>
              <select name="priority" value={form.priority} onChange={handleChange} style={inputStyle}>
                {priorityOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {/* Date */}
            <div>
              <label style={labelStyle}>
                <Calendar size={14} style={{ marginRight: 4 }} />
                Date
              </label>
              <input type="date" name="tanggal" value={form.tanggal} onChange={handleChange} style={inputStyle} />
            </div>

            {/* Bagian / Department */}
            <div>
              <label style={labelStyle}>
                <Building2 size={14} style={{ marginRight: 4 }} />
                Bagian / Department
              </label>
              <select name="bagian" value={form.bagian} onChange={handleChange} style={inputStyle}>
                <option value="">— Pilih Bagian —</option>
                {bagianList.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
              </select>
            </div>

            {/* Start Time */}
            <div>
              <label style={labelStyle}>
                <Clock size={14} style={{ marginRight: 4 }} />
                Start Date & Time
              </label>
              <input
                type="datetime-local"
                name="start_time"
                value={form.start_time}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            {/* End Time */}
            <div>
              <label style={labelStyle}>
                <Clock size={14} style={{ marginRight: 4 }} />
                End Date & Time
              </label>
              <input
                type="datetime-local"
                name="end_time"
                value={form.end_time}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            {/* Status */}
            <div>
              <label style={labelStyle}>Status</label>
              <select name="status" value={form.status} onChange={handleChange} style={inputStyle}>
                {statusOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {/* Assigned To (Primary) */}
            <div>
              <label style={labelStyle}>
                <User size={14} style={{ marginRight: 4 }} />
                Penanggung Jawab Utama
              </label>
              <select name="assigned_to" value={form.assigned_to} onChange={handleChange} style={inputStyle}>
                <option value="">— Pilih User —</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.full_name || u.username} ({u.role}){currentUser?.id === u.id ? ' (Anda)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Tag - Bagian (Multi-select) */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>
                <Users size={14} style={{ marginRight: 4 }} />
                Tag Tim / Bagian
                <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#9ca3af', marginLeft: 4 }}>
                  (pilih beberapa)
                </span>
              </label>
              <div ref={bagianRef} style={{ position: 'relative' }}>
                <div
                  onClick={() => setBagianDropdownOpen(!bagianDropdownOpen)}
                  style={{
                    ...inputStyle, cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    minHeight: 38
                  }}
                >
                  <span style={{ color: selectedBagianIds.length > 0 ? '#374151' : '#9ca3af', fontSize: '0.85rem' }}>
                    {selectedBagianIds.length > 0
                      ? `${selectedBagianIds.length} bagian dipilih`
                      : '— Pilih Bagian —'
                    }
                  </span>
                  <ChevronDown size={16} style={{ color: '#9ca3af', transform: bagianDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </div>
                {bagianDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: 'white', border: '1px solid #d1d5db', borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 200, overflowY: 'auto',
                    marginTop: 4
                  }}>
                    {bagianList.length === 0 ? (
                      <div style={{ padding: 12, textAlign: 'center', color: '#9ca3af', fontSize: '0.8rem' }}>
                        Tidak ada data bagian
                      </div>
                    ) : bagianList.map(b => {
                      const isSelected = selectedBagianIds.includes(b.id);
                      const userCount = users.filter(u => u.bagian_id === b.id && u.is_active !== false).length;
                      return (
                        <div
                          key={b.id}
                          onClick={() => toggleBagian(b.id)}
                          style={{
                            padding: '8px 12px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 10,
                            background: isSelected ? '#f0fdf4' : 'transparent',
                            borderBottom: '1px solid #f3f4f6',
                            transition: 'background 0.1s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = isSelected ? '#dcfce7' : '#f9fafb'}
                          onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? '#f0fdf4' : 'transparent'}
                        >
                          <div style={{
                            width: 18, height: 18, borderRadius: 4,
                            border: isSelected ? '2px solid #059669' : '2px solid #d1d5db',
                            background: isSelected ? '#059669' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            {isSelected && <CheckCircle size={12} style={{ color: 'white' }} />}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>{b.name}</div>
                            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{userCount} anggota</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Tag - User (Multi-select) */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>
                <User size={14} style={{ marginRight: 4 }} />
                Tag User Langsung
                <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#9ca3af', marginLeft: 4 }}>
                  (tambahan, pilih beberapa)
                </span>
              </label>
              <div ref={userRef} style={{ position: 'relative' }}>
                <div
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  style={{
                    ...inputStyle, cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    minHeight: 38
                  }}
                >
                  <span style={{ color: selectedUserIds.length > 0 ? '#374151' : '#9ca3af', fontSize: '0.85rem' }}>
                    {selectedUserIds.length > 0
                      ? `${selectedUserIds.length} user dipilih`
                      : '— Pilih User —'
                    }
                  </span>
                  <ChevronDown size={16} style={{ color: '#9ca3af', transform: userDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </div>
                {userDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: 'white', border: '1px solid #d1d5db', borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 220, overflowY: 'auto',
                    marginTop: 4
                  }}>
                    {users.length === 0 ? (
                      <div style={{ padding: 12, textAlign: 'center', color: '#9ca3af', fontSize: '0.8rem' }}>
                        Tidak ada data user
                      </div>
                    ) : users.filter(u => u.is_active !== false).map(u => {
                      const isSelected = selectedUserIds.includes(u.id);
                      return (
                        <div
                          key={u.id}
                          onClick={() => toggleUser(u.id)}
                          style={{
                            padding: '8px 12px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 10,
                            background: isSelected ? '#f0fdf4' : 'transparent',
                            borderBottom: '1px solid #f3f4f6'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = isSelected ? '#dcfce7' : '#f9fafb'}
                          onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? '#f0fdf4' : 'transparent'}
                        >
                          <div style={{
                            width: 18, height: 18, borderRadius: 4,
                            border: isSelected ? '2px solid #059669' : '2px solid #d1d5db',
                            background: isSelected ? '#059669' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            {isSelected && <CheckCircle size={12} style={{ color: 'white' }} />}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>
                              {u.full_name || u.username}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{u.role || ''}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Tagged Users Preview */}
            {taggedUsers.length > 0 && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ ...labelStyle, color: '#059669' }}>
                  <CheckCircle size={14} style={{ marginRight: 4 }} />
                  Akan Ditandai ({taggedUsers.length} user)
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {taggedUsers.map(u => (
                    <div
                      key={u.id}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '4px 10px', borderRadius: 20,
                        background: '#f0fdf4', border: '1px solid #bbf7d0',
                        fontSize: '0.78rem', color: '#166534'
                      }}
                    >
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        background: '#059669', color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.6rem', fontWeight: 700
                      }}>
                        {(u.full_name || u.username || '?').charAt(0).toUpperCase()}
                      </div>
                      <span>{u.full_name || u.username}</span>
                      {u.tagSource && (
                        <span style={{ fontSize: '0.65rem', color: '#6b7280', marginLeft: 2 }}>
                          ({u.tagSource})
                        </span>
                      )}
                      <X
                        size={12}
                        style={{ cursor: 'pointer', color: '#9ca3af', marginLeft: 2, flexShrink: 0 }}
                        onClick={() => removeTaggedUser(u.id)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachment */}
            <div>
              <label style={labelStyle}>
                <FileText size={14} style={{ marginRight: 4 }} />
                Attachment
              </label>
              <div style={{
                border: '2px dashed #d1d5db', borderRadius: 8, padding: 16,
                textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
                background: file ? '#f0fdf4' : '#fafafa'
              }}
                onClick={() => document.getElementById('file-input').click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const dropped = e.dataTransfer.files[0];
                  if (dropped) { setFile(dropped); setFileName(dropped.name); }
                }}
              >
                <input
                  id="file-input"
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx"
                />
                <Upload size={24} style={{ color: '#9ca3af', marginBottom: 8 }} />
                {file ? (
                  <div style={{ color: '#059669', fontWeight: 500, fontSize: '0.85rem' }}>
                    <FileText size={14} style={{ marginRight: 4 }} />
                    {fileName}
                  </div>
                ) : (
                  <div>
                    <div style={{ color: '#6b7280', fontSize: '0.85rem', fontWeight: 500 }}>Klik untuk upload file</div>
                    <div style={{ color: '#9ca3af', fontSize: '0.72rem', marginTop: 4 }}>PDF, JPG, PNG, Excel, Word</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 12, marginTop: 28, justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: 20 }}>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/activity')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || uploading} style={{
              background: '#059669', color: 'white', border: 'none',
              padding: '10px 28px', borderRadius: 8, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
              opacity: saving || uploading ? 0.7 : 1
            }}>
              {uploading ? <Upload size={16} className="spinner" /> : <CheckCircle size={16} />}
              {uploading ? 'Uploading...' : saving ? 'Menyimpan...' : 'Simpan Activity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'flex', alignItems: 'center', gap: 4,
  fontSize: '0.82rem', fontWeight: 600, color: '#374151',
  marginBottom: 6
};

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid #d1d5db', fontSize: '0.88rem',
  outline: 'none', boxSizing: 'border-box',
  background: 'white', transition: 'border-color 0.15s'
};
